<template>
  <div>
    <v-toolbar flat density="compact" class="px-4">
      <v-btn icon="mdi-close" @click="closeContactImport" color="error"></v-btn>
      <v-toolbar-title>Import Contacts</v-toolbar-title>
      <v-btn variant="outlined" color="primary" @click="openImportHistory"><v-icon>mdi-history</v-icon> History</v-btn>
    </v-toolbar>

    <v-stepper v-if="!openContactImportHistory" :items="['Select', 'Map', 'Import']" v-model="currentStep" hide-actions>

      <!-- Step 1: Select File -->
      <template v-slot:item.1>
        <v-card title="Select File" flat>
          <v-card-text>
            <v-file-input 
              :rules="RULES.MAX_SIZE(5000000)" 
              type="file" 
              accept=".csv, .xlsx, .xls"
              placeholder="Select File" 
              show-size
              variant="outlined" 
              color="indigo-darken-3" 
              @update:model-value="onFileChanged"
               />
            <v-divider class="my-3"></v-divider>
            <div><b>File formats supported:</b> XLS, XLSX, CSV</div>
          </v-card-text>
          <v-card-actions class="justify-end">
            <v-btn color="primary" variant="flat" @click="currentStep = 2" :disabled="!file">
              Continue
            </v-btn>
          </v-card-actions>
        </v-card>
      </template>

      <!-- Step 2: Map Fields -->
      <template v-slot:item.2>
        <v-card title="Map Fields" flat>
          <v-card-text>
            <v-row v-for="field in contactFields" :key="field.value" class="align-center">
              <v-col cols="6" style="display: flex;">
                <v-list-item :title="field.title" density="compact"></v-list-item><v-icon v-if="field?.required" style="display: inline; margin-left: -10px; margin-top: 5px;" size="10" color="red">mdi-star</v-icon>
              </v-col>
              <v-col cols="6">
                <v-select 
                  v-model="fieldMappings[field.value]" 
                  :items="fileHeaders" 
                  label="Select matching column"
                  variant="outlined" 
                  clearable
                  density="compact"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <v-list-item title="Contact Type" density="compact"></v-list-item>
              </v-col>
              <v-col cols="6">
                <v-select 
                  v-model="selectedType" 
                  :items="contactTypes" 
                  item-value="type_id" 
                  item-title="type_name"
                  placeholder="Select to filter" 
                  label="Contact Types" 
                  variant="outlined" 
                  style="cursor: pointer;"
                  color="success" 
                  clearable
                  density="compact"
                >
                  <template v-slot:item="{ item, props }">
                    <div v-bind="props" :style="{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '5px 8px',
                      cursor: 'pointer'

                    }">

                      <v-avatar rounded="lg" size="x-small" :color="item.raw.color">

                        <v-btn icon size="x-small" variant="text">
                          <v-icon>mdi-account-outline</v-icon>
                        </v-btn>
                      </v-avatar>

                      <span :style="{ textAlign: 'left', flex: 1, padding: '3px' }">
                        {{ item.title }}
                      </span>
                    </div>
                  </template>

                </v-select>
                <div style="margin-top: -10px; font-size: 11px;">Choose a Contact Type otherwise default type will be applied to all contacts.</div>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="justify-space-between">
            <v-btn variant="flat" @click="currentStep = 1">Back</v-btn>
            <v-btn color="primary" variant="flat" @click="currentStep = 3" :disabled="!isMappingValid">
              Continue
            </v-btn>
          </v-card-actions>
        </v-card>
      </template>

      <!-- Step 3: Import -->
      <template v-slot:item.3>
        <v-card title="Import Contacts" flat>
          <v-card-text>
            <div v-if="isImporting">
              <v-progress-linear indeterminate color="primary" height="20" striped />
              <p class="mt-2">Uploading file and starting import...</p>
            </div>
            <div v-else-if="importComplete">
              <v-alert type="success" variant="tonal" v-if="importResult.success">
                {{ importResult.message }}
              </v-alert>
              <v-alert type="error" variant="tonal" v-else>
                {{ importResult.message }}
              </v-alert>
            </div>
            <div v-else>
              <p>Click "Start Import" to begin uploading file & mapping to server.</p>
            </div>
          </v-card-text>
          <v-card-actions class="justify-space-between">
            <v-btn variant="flat" @click="importComplete ? currentStep = 1 : currentStep = 2">Back</v-btn>
            <v-btn color="primary"  :loading="loading" variant="flat" @click="submitImport" :disabled="isImporting || importComplete">
              Start Import
            </v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-stepper>
    <v-card v-if="openContactImportHistory">
      <v-card-title>Import History</v-card-title>
      <v-card-text>
        <v-timeline side="end">
    <v-timeline-item
      v-for="item in importHistory"
      :key="item.id"
      size="small"
    >
     <template v-slot:opposite>
       <div
          :class="`pt-1 headline font-weight-bold text-${getStatusColor(item.status)}`"
          v-text="formatShortMonthDate(item.imported_at)"
        ></div>
      </template>
      <template v-slot:icon>
        <UserPopup :user_id="item.user_id" :tooltip="false" />
      </template>
      <v-alert
        :color="getStatusColor(item.status)"
        :icon="getStatusIcon(item.status)"
        :value="true"
         variant="tonal"
         border="start"
         style="min-width: 700px;"
      >
        
        <div v-if="item.status === CONTACT_IMPORT_STATUS.SUCCESS">
          {{ getUserName(item.user_id) }} has imported <strong>{{ item.total_records }}</strong> contacts. <br>
          File Name: <strong>{{ item.file_name }}</strong> <br>
         <div v-if="!item.email_fetched" style="display: flex; justify-content: end;">
            <div v-if="item.import_id == emailFetchProgressContact.import_id && emailFetchProgressContact.progress > 0" style="width: 100%; margin-top: 10px;">
              <v-progress-linear
                  v-model="emailFetchProgressContact.progress"
                  color="primary"
                  height="20"
                  striped
                  rounded
              >
                  <strong style="color: #000;">{{ Math.floor(emailFetchProgressContact.progress) }}%</strong>
              </v-progress-linear>
              <p class="mt-2">{{ emailFetchProgressContact.status }}</p>

            </div>
            <div v-else>
              <v-btn variant="outlined" color="primary" @click="fetchEmailsContacts(item.import_id)">Fetch Past Emails</v-btn>
              <v-btn variant="outlined" color="warning" style="margin-left: 5px;" @click="rollBackImportRecord(item.import_id)">RollBack</v-btn>
            </div>
         </div>
        </div>

        <div v-if="item.status === CONTACT_IMPORT_STATUS.FAILED">
         {{ getUserName(item.user_id) }} attempted to import <strong>{{ item.total_records }}</strong> contacts but the import failed.
        </div>
         <div v-if="item.status === CONTACT_IMPORT_STATUS.ROLLBACK">
         {{ getUserName(item.user_id) }} has imported <strong>{{ item.total_records }}</strong> contacts which was rolled back by {{ getUserName(item.reverted_by) }} on {{ formatShortMonthDate(item.reverted_at) }}.<br>
          File Name: <strong>{{ item.file_name }}</strong>
        </div>
      </v-alert>
    </v-timeline-item>
  </v-timeline>
  </v-card-text>
    </v-card>
    <ConfirmationAlert ref="confirmationAlertRef" />
  </div>
</template>

<script>
import { RULES } from "../../../constants/validationRules";
import * as XLSX from "xlsx";
import crmService from "@/services/crmService";
import { mapGetters } from "vuex";
import { CONTACT_IMPORT_STATUS } from '../../../constants/constantsValues.js';
import dayjsMixin from "@/mixins/dayjsMixin";
import UserPopup from "../../Avatar/UserPopup.vue";
import ConfirmationAlert from "../../CustomComponents/ConfirmationAlert.vue";

export default {
  name: "ContactImportPage",
  mixins: [dayjsMixin],
  components: { UserPopup, ConfirmationAlert },
  data() {
    return {
      RULES,
      file: null,
      fileHeaders: [],
      fieldMappings: {},
      contactFields: [
        { value: "first_name", title: "First Name", required : true },
        { value: "last_name", title: "Last Name" },
        { value: "ph1", title: "Phone Number", required : true },
        { value: "ph2", title: "Home Number" },
        { value: "ph3", title: "Work Number" },
        { value: "email", title: "Email" },
        { value: "Birthdate", title: "Date Of Birth" },
        { value: "gender", title: "Gender" },
        { value: "address", title: "Address 1" },
        { value: "address2", title: "Address 2" },
        { value: "city", title: "City" },
        { value: "zip", title: "ZIP" },
        { value: "state", title: "State" },
      ],
      selectedType : null,
      currentStep: 1,
      isImporting: false,
      importComplete: false,
      importResult: {},
      loading: false,
      openContactImportHistory : false,
      importHistory : [],
      CONTACT_IMPORT_STATUS
    };
  },
  computed: {
    ...mapGetters("people", ["contactTypes","getUserName"]),
    ...mapGetters(["emailFetchProgressContact"]),
  
    isMappingValid() {
      return this.contactFields
        .filter((f) => f.required)
        .every((f) => this.fieldMappings[f.value]);
    },
    getStatusColor() {
      return (status) => {
        switch (status) {
          case CONTACT_IMPORT_STATUS.SUCCESS:
            return "success";
          case CONTACT_IMPORT_STATUS.FAILED:
            return "error";
          case  CONTACT_IMPORT_STATUS.ROLLBACK:
            return "warning";
          default:
            return "info";
        }
      };
    },
    getStatusIcon() {
      return (status) => {
        switch (status) {
          case CONTACT_IMPORT_STATUS.SUCCESS:
            return "mdi-check-circle-outline";
          case CONTACT_IMPORT_STATUS.FAILED:
            return " mdi-close-circle-outline";
          case  CONTACT_IMPORT_STATUS.ROLLBACK:
            return "mdi-delete-circle-outline";
          default:
            return "mdi-information";
        }
      };
    },
    
  },
  methods: {
   onFileChanged(file) {
    this.isImporting = false;
    this.importComplete = false;
    this.file = file || null;
    if (!this.file) {
      this.fileHeaders = [];
      this.fieldMappings = {};
      return;
    }
    this.readFileHeaders();
  },

    readFileHeaders() {
      if (!this.file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (json.length > 0) {
          this.fileHeaders = json[0].map((h) => String(h).trim());
          this.fieldMappings = Object.fromEntries(
            this.contactFields.map((f) => [f.value, null])
          );
        }
      };
      reader.readAsArrayBuffer(this.file);
    },

    async submitImport() {
      if (!this.file) return;

      this.isImporting = true;
      this.importComplete = false;
      this.loading = true;

      try {
        const formData = new FormData();
        formData.append("file", this.file);
        formData.append("mapping", JSON.stringify(this.fieldMappings));
        if (this.selectedType !== null && this.selectedType !== undefined) {
          formData.append("contact_type", this.selectedType);
        }
       
        const res = await crmService.importContacts(formData);
       
        this.importResult = res.data;
        this.importResult.success = res.status === 201;
      } catch (err) {
        console.error(err);
        
        this.importResult = { success: false, message: "Import failed" };
      } finally {
        this.isImporting = false;
        this.importComplete = true;
        this.file = null;
        this.fileHeaders = [];
        this.loading = false;
        
      }
    },
    closeContactImport(){
      this.$router.back();
    },
    async openImportHistory(){
      try {
        if(this.openContactImportHistory){
          this.openContactImportHistory = false;
          return;
        }
        let res = await crmService.getContactImportHistory();
        this.openContactImportHistory = true;
        if(res.data && res.status === 200){
          this.importHistory = res.data;
        }
      } catch (error) {
        console.error(error);
      }
     

    },
     async rollBackImportRecord(import_id){
      try {
        
        const confirmed = await this.$refs.confirmationAlertRef.open({
          title: 'Are you sure?',
          text: "Do you want to roll back this import? If you roll back this import, all contacts imported in this batch will be deleted. If any of these contacts are linked to other records (such as calls or textmessages), you will not be able to delete entire import."
        });
        if (!confirmed) {
          return;
        }
        let res = await crmService.rollBackContactImportRecord(import_id);
        if(res.status === 200){
          this.openImportHistory();
        }
      } catch (error) {
        console.error(error);
      }
    },
    async fetchEmailsContacts(import_id){
      try {
        await crmService.fetchEmailsForImportedContacts(import_id);
      } catch (error) {
        console.error(error);
      }
    }
   }
};
</script>
